"use client";

import { useState } from "react";
import dayjs from "dayjs";
import Link from "next/link";
import Image from "next/image";
import { ibmPlexMono } from "@/assets/fonts";
import { IBlogs } from "../../page";
import { LayoutGrid, List } from "lucide-react";
import { cn } from "@/libs/utils";
import { getNewSlug } from "@/config/post-redirects";

interface PostsListProps {
  blogs: IBlogs[];
}

type ViewMode = "list" | "grid";

export default function PostsList({ blogs }: PostsListProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  // 날짜별 정렬 (최신순) - date 우선, 없으면 lastModified 사용
  const sortedBlogs = [...blogs].sort((a, b) => {
    const dateA = a.date || a.lastModified;
    const dateB = b.date || b.lastModified;

    if (!dateA) return 1;
    if (!dateB) return -1;
    return dayjs(dateB).valueOf() - dayjs(dateA).valueOf();
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = dayjs(dateString);
    return date.format("YYYY.MM.DD");
  };

  // Get the URL slug for a blog post
  // Returns new slug if available, otherwise returns URL-encoded title
  const getPostUrl = (title: string): string => {
    const newSlug = getNewSlug(title);
    return newSlug || encodeURIComponent(title);
  };

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-size-xl font-semibold mb-1">Posts</h1>
          <p className="text-size-xs text-muted-foreground">
            {blogs.length} posts
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-md transition-colors ${
              viewMode === "list"
                ? "bg-muted text-muted-foreground"
                : "hover:bg-muted"
            }`}
            aria-label="List view"
          >
            <List size={20} className="stroke-[1.5px]" />
          </button>
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2 rounded-md transition-colors ${
              viewMode === "grid"
                ? "bg-muted text-muted-foreground"
                : "hover:bg-muted"
            }`}
            aria-label="Grid view"
          >
            <LayoutGrid size={20} className="stroke-[1.5px]" />
          </button>
        </div>
      </div>

      {viewMode === "list" ? (
        <div className="flex flex-col gap-2 mb-32">
          {sortedBlogs.length > 0 &&
            sortedBlogs?.map((item, i) => {
              const displayDate = item.date || item.lastModified;
              return (
                <Link key={i} href={`/posts/${getPostUrl(item?.title)}`}>
                  <div className="group flex gap-4 p-2 hover:bg-muted transition-colors rounded-sm">
                    <div className="flex-shrink-0 w-20 h-20 rounded-md overflow-hidden relative bg-accent">
                      <Image
                        src={
                          item?.thumbnail
                            ? `${process.env.NEXT_PUBLIC_BLOG_CF_ENDPOINT}/files/${item.thumbnail}`
                            : "/bear0.png"
                        }
                        alt={item.title}
                        fill
                        className={cn(
                          !item?.thumbnail
                            ? "p-5 object-contain"
                            : "object-cover"
                        )}
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-2 justify-between">
                      <div className="flex items-center justify-between gap-4">
                        <span
                          className={`text-[14px] truncate group-hover:underline transition-colors tracking-tight ${ibmPlexMono.className}`}
                        >
                          {item?.title}
                        </span>
                        {displayDate && (
                          <time className="font-thin text-size-xs text-muted-foreground tabular-nums whitespace-nowrap flex-shrink-0">
                            {formatDate(displayDate)}
                          </time>
                        )}
                      </div>
                      {item?.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {item.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="font-thin text-size-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-32">
          {sortedBlogs.length > 0 &&
            sortedBlogs?.map((item, i) => {
              const displayDate = item.date || item.lastModified;
              return (
                <Link key={i} href={`/posts/${getPostUrl(item?.title)}`}>
                  <div className="rounded-sm group flex flex-col h-full p-1.5 overflow-hidden hover:bg-muted transition-colors">
                    <div className="rounded-sm overflow-hidden w-full h-32 relative bg-accent">
                      <Image
                        src={
                          item?.thumbnail
                            ? `${process.env.NEXT_PUBLIC_BLOG_CF_ENDPOINT}/files/${item.thumbnail}`
                            : "/bear0.png"
                        }
                        alt={item.title}
                        fill
                        className={cn(
                          !item?.thumbnail
                            ? "p-10 object-contain"
                            : "object-cover"
                        )}
                      />
                    </div>

                    <div className="p-1 flex flex-col gap-3 flex-1">
                      <div className="flex flex-col gap-2">
                        <span
                          className={`text-[14px] tracking-tight group-hover:text-primary transition-colors line-clamp-2 ${ibmPlexMono.className}`}
                        >
                          {item?.title}
                        </span>
                        {displayDate && (
                          <time className="text-size-xs font-thin text-muted-foreground tabular-nums">
                            {formatDate(displayDate)}
                          </time>
                        )}
                      </div>
                      {item?.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-auto">
                          {item.tags.slice(0, 3).map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="font-thin text-size-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                          {item.tags.length > 3 && (
                            <span className="text-size-xs px-2 py-0.5 text-muted-foreground">
                              +{item.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
        </div>
      )}
    </>
  );
}
